import { useEffect, useState } from "react";
import { GraduationCap, ChartLine, Users, Hourglass, CalendarDays, UserX, Download } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import { PaginationControls } from "../../components/PaginationControls";
import { Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { LMSBASEURL } from "../../config/apiUrls";
import { isMobile } from "react-device-detect";
import { Funnel } from "lucide-react";

const StudentActivityDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasActivity, setHasActivity] = useState(false);
  const instituteId = sessionStorage.getItem("instituteId") || sessionStorage.getItem("studentInstituteId");

  // Not-logged-in state
  const [notLoggedIn, setNotLoggedIn] = useState([]);
  const [notLoggedInTotal, setNotLoggedInTotal] = useState(0);
  const [notLoggedInPage, setNotLoggedInPage] = useState(1);
  const [notLoggedInLastPage, setNotLoggedInLastPage] = useState(1);
  const [notLoggedInLoading, setNotLoggedInLoading] = useState(false);
  const [notLoggedInError, setNotLoggedInError] = useState("");
  const [exportingNotLoggedIn, setExportingNotLoggedIn] = useState(false);
  const [perPage, setPerPage] = useState(10);

  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 14);
  const formatInputDate = (date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(formatInputDate(sevenDaysAgo));
  const [endDate, setEndDate] = useState(formatInputDate(today));
  const [selectedStudyingYear, setSelectedStudyingYear] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date());
  const token = process.env.REACT_APP_API_TOKEN;

  useEffect(() => {
    fetchData(startDate, endDate);
    fetchNotLoggedIn(1);
  }, []);

  const fetchNotLoggedIn = async (page = 1, customPerPage) => {
    try {
      setNotLoggedInLoading(true);
      setNotLoggedInError("");
      const headers = new Headers();
      headers.append("Authorization", token);
      headers.append("Accept", "application/json");
      headers.append("Content-Type", "application/json");
      const body = JSON.stringify({
        institute_id: Number(instituteId),
        per_page: customPerPage ?? perPage,
        page,
      });
      const response = await fetch(`${LMSBASEURL}/api/students/not-logged-in`, {
        method: "POST",
        headers,
        body,
        redirect: "follow",
      });
      const json = await response.json();
      if (!json.status) throw new Error(json.message || "API error");
      setNotLoggedIn(json.data || []);
      setNotLoggedInTotal(json.pagination?.total || 0);
      setNotLoggedInLastPage(json.pagination?.last_page || 1);
      setNotLoggedInPage(page);
    } catch (err) {
      setNotLoggedInError(err.message);
    } finally {
      setNotLoggedInLoading(false);
    }
  };

  const handleExportNotLoggedIn = async () => {
    try {
      setExportingNotLoggedIn(true);
      const headers = new Headers();
      headers.append("Authorization", token);
      headers.append("Accept", "application/json");
      headers.append("Content-Type", "application/json");
      const body = JSON.stringify({ institute_id: Number(instituteId) });
      const response = await fetch(`${LMSBASEURL}/api/students/not-logged-in/export`, {
        method: "POST",
        headers,
        body,
        redirect: "follow",
      });
      if (!response.ok) throw new Error("Export failed");
      const contentType = response.headers.get("Content-Type") || "";
      const now = new Date();
      const day = String(now.getDate()).padStart(2, "0");
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = monthNames[now.getMonth()];
      const year = now.getFullYear();
      const dateStr = `${day}${month}${year}`;
      const fileName = `students_not_logged_in_${dateStr}.xlsx`;

      if (contentType.includes("application/json")) {
        const json = await response.json();
        if (json.file_url) {
          const link = document.createElement("a");
          link.href = json.file_url;
          link.download = fileName;
          link.click();
        }
      } else {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert("Export failed: " + err.message);
    } finally {
      setExportingNotLoggedIn(false);
    }
  };

  const fetchData = async (from = startDate, to = endDate,) => {
    try {
      setLoading(true);
      setError("");

      const headers = new Headers();
      headers.append("Authorization", token);
      headers.append("Accept", "application/json");
      headers.append("Content-Type", "application/json");

      const body = JSON.stringify({
        institute_ids: [instituteId],
        start_date: from,
        end_date: to,
      });

      const response = await fetch(`${LMSBASEURL}/api/student-activity`, {
        method: "POST",
        headers,
        body,
        redirect: "follow",
      });

      const json = await response.json();
      if (!json.status) throw new Error(json.message || "API error");

      setSummary(json.summary);
      setChartData(transformChartData(json.data));
      setHasActivity(checkIfActivityExists(json.data));
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkIfActivityExists = (data) => {
    if (!data || data.length === 0) return false;
    return data.some((day) => day.total_logged_in_students > 0);
  };

  const transformChartData = (apiData) => {
    if (!apiData || apiData.length === 0) return [];

    const uniqueKeys = new Set();
    apiData.forEach((day) => {
      day.data.forEach((entry) => {
        uniqueKeys.add(`${entry.degree_title} (${entry.studying_year})`);
      });
    });

    return apiData.map((day) => {
      const dayObj = { date: formatDate(day.attendance_date) };
      uniqueKeys.forEach((key) => (dayObj[key] = 0));

      day.data.forEach((entry) => {
        const key = `${entry.degree_title} (${entry.studying_year})`;
        dayObj[key] = entry.total_logged_in_students;
      });

      return dayObj;
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const handleFilter = () => {
    fetchData(startDate, endDate, selectedStudyingYear?.value, selectedYear.getFullYear());
  };

  const handleReset = () => {
    setSelectedStudyingYear(null)
    setSelectedYear(new Date())
    const newToday = new Date();
    const newSevenDaysAgo = new Date();
    newSevenDaysAgo.setDate(newToday.getDate() - 6);

    const from = formatInputDate(newSevenDaysAgo);
    const to = formatInputDate(newToday);

    setStartDate(from);
    setEndDate(to);
    fetchData(from, to);
  };

  const summaryCards = [
    {
      label: "Total Unique Logins",
      value: summary?.total_unique_students || 0,
      icon: <GraduationCap />,
      color: "primary",
    },
    {
      label: "Avg. Daily Logins",
      value: summary?.average_daily_logins || 0,
      icon: <ChartLine />,
      color: "warning",
    },
    {
      label: "Total Logins Till Date",
      value: summary?.total_logins_till_date || 0,
      icon: <Users />,
      color: "primary",
    },
    {
      label: "Total Time Spent",
      value: summary?.total_time_spend || "0s",
      icon: <Hourglass />,
      color: "warning",
    },
    {
      label: "Avg. Time Per Day",
      value: summary?.avg_time_per_day || "0s",
      icon: <CalendarDays />,
      color: "primary",
    },
  ];

  const handleEndDate = (e) => {
    setEndDate(e.target.value)
  }
  const handleStateDate = (e) => {
    if (e.target.value) {
      setStartDate(e.target.value);
    }
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <PageHeader
            title="Login Activity"
            breadcrumbs={[
              { label: "Dashboard", path: "/institute-dashboard" },
              { label: "Login Activity", active: true },
            ]}
          />
        </div>
      </div>

      <div className="my-2">
        {error && (
          <Alert variant="danger" className="my-3">
            {error}
          </Alert>
        )}
        <div>
          <div className="d-none d-lg-block">
            <div className="mb-4 bg-white rounded-4 border border-2 p-4">
              <Form>
                <Row className="g-3 align-items-end">
                  <Col xs={12} sm={6} md={3}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onClick={(e) => {
                        e.target.type = "date";
                        e.target.showPicker?.();
                      }}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={handleStateDate}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onClick={(e) => {
                        e.target.type = "date";
                        e.target.showPicker?.();
                      }}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={handleEndDate}
                    />
                  </Col>
                  <Col md={3}></Col>
                  <Col xs={12} md={3}>
                    <div className="d-flex flex-column flex-sm-row justify-content-sm-end gap-2">
                      <Button
                        variant="primary"
                        className="w-100 w-sm-auto"
                        onClick={handleFilter}
                      >
                        {loading ? <div className="d-flex align-items-center gap-1">Applying<Spinner animation="border" variant="light" size="sm" /></div> : "Apply"}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="w-100 w-sm-auto"
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>

          <div className="d-flex d-lg-none justify-content-end align-items-center mb-3">
            <button
              className="btn btn-primary btn-sm"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#mobileFilterCanvas"
            >
              Filter Option <Funnel size={16} />
            </button>
          </div>
          <div
            className="offcanvas offcanvas-end"
            tabIndex="-1"
            id="mobileFilterCanvas"
          >
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filters</h5>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
              ></button>
            </div>
            <div className="offcanvas-body">
              <Form>
                <Row className="g-3 align-items-end">
                  <Col xs={12} sm={6} md={3}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onClick={(e) => {
                        e.target.type = "date";
                        e.target.showPicker?.();
                      }}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={handleStateDate}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onClick={(e) => {
                        e.target.type = "date";
                        e.target.showPicker?.();
                      }}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={handleEndDate}
                    />
                  </Col>
                  <Col md={3}></Col>
                  <Col xs={12} md={3}>
                    <div className="d-flex flex-column flex-sm-row justify-content-sm-end gap-2">
                      <Button
                        variant="primary"
                        className="w-100 w-sm-auto"
                        onClick={handleFilter}
                      >
                        {loading ? <div className="d-flex align-items-center gap-1">Applying<Spinner animation="border" variant="light" size="sm" /></div> : "Apply"}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="w-100 w-sm-auto"
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>

        {!isMobile && (
          <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
            {summaryCards.map((item, index) => (
              <div
                key={index}
                className="w-100"
                style={{
                  flex: "1 1 calc(18% - 12px)",
                  minWidth: "200px",
                  maxWidth: "240px",
                }}
              >
                <Card className="h-100 border-2 rounded-4">
                  <Card.Body className="p-2 text-center d-flex flex-column align-items-center justify-content-center">
                    <div
                      style={{
                        fontSize: "2rem",
                        padding: "10px",
                        borderRadius: "12px",
                        backgroundColor:
                          item.color === "primary"
                            ? "rgba(0, 123, 255, 0.1)"
                            : "rgba(255, 193, 7, 0.1)",
                        color: item.color === "primary" ? "#007bff" : "#ffc107",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "10px",
                        width: "60px",
                        height: "60px",
                      }}
                    >
                      {item.icon}
                    </div>
                    <h6 className="text-muted mb-1" style={{ fontSize: "0.9rem" }}>
                      {item.label}
                    </h6>
                    <h4 className="fw-bold mb-0">{item.value}</h4>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        )}

        {isMobile && (
          <Row className="justify-content-center g-2 mb-4">
            {summaryCards.map((item, index) => (
              <Col xs={6} key={index}>
                <Card className="h-100 border-2 rounded-4 text-center p-2">
                  <Card.Body className="p-2 d-flex flex-column align-items-center justify-content-center">
                    <div
                      style={{
                        fontSize: "2rem",
                        padding: "10px",
                        borderRadius: "12px",
                        backgroundColor:
                          item.color === "primary"
                            ? "rgba(0, 123, 255, 0.1)"
                            : "rgba(255, 193, 7, 0.1)",
                        color: item.color === "primary" ? "#007bff" : "#ffc107",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "10px",
                        width: "50px",
                        height: "50px",
                      }}
                    >
                      {item.icon}
                    </div>
                    <h6 className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
                      {item.label}
                    </h6>
                    <h5 className="fw-bold mb-0">{item.value}</h5>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}



        <Card className="border-2 rounded-4 p-2">
          <Card.Body>
            <div className={`d-flex justify-content-between align-items-center mb-3 w-100 gap-3 ${isMobile && 'flex-wrap'}`}>
              <div>
                <h5 className="fw-bold" style={{ color: "#007bff" }}>
                  <i className="bi bi-bar-chart-fill me-2"></i>
                  Daily Logins by Degree & Studying Year
                </h5>
                <span className="text-muted small">
                  {summary?.summary_text}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="text-center my-5 d-flex align-items-center justify-content-center" style={{ height: 200 }}>
                <Spinner animation="border" variant="primary" />
              </div>
            )
              :
              <>
                {!hasActivity ? (
                  <div className="py-4">
                    <h6 className="fw-bold text-center mb-3 text-muted">
                      No login activity between {summary?.date_range?.from} and {summary?.date_range?.to}.
                    </h6>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={420}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ angle: -45, textAnchor: "end", fontSize: 12, marginBottom: 40 }} interval={0} stroke="#666" />
                      {!isMobile && (
                        <YAxis
                          label={{
                            value: "Students Logged In",
                            angle: -90,
                            position: "insideLeft",
                          }}
                          tick={{ fontSize: 12 }}
                          stroke="#666"
                        />
                      )}
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          borderRadius: "8px",
                          fontSize: "12px",
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: "12px", paddingTop: 20 }} iconType="rect" />
                      {chartData.length > 0 &&
                        Object.keys(chartData[0])
                          .filter((key) => key !== "date")
                          .map((key, idx) => (
                            <Bar
                              key={idx}
                              dataKey={key}
                              barSize={10}
                              stackId="a"
                              fill={["#007bff", "#17a2b8", "#28a745", "#ffc107", "#fd7e14"][idx % 5]}
                            />
                          ))}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </>
            }
          </Card.Body>
        </Card>

        {/* Not Logged In Expanded Table — shown after chart */}
        <Card className="border-2 rounded-4 p-2 mt-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: "#dc3545" }}>
                    <UserX size={18} className="me-2" />
                    Students Not Logged In Yet <span className="text-muted small">( Total: {notLoggedInTotal} student{notLoggedInTotal !== 1 ? "s" : ""} )</span>
                  </h5>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex align-items-center gap-1">
                    <label className="text-muted small mb-0 text-nowrap">Per page:</label>
                    <Form.Select
                      size="sm"
                      style={{ width: "80px" }}
                      value={perPage}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setPerPage(val);
                        fetchNotLoggedIn(1, val);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Form.Select>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleExportNotLoggedIn}
                    disabled={exportingNotLoggedIn}
                  >
                    {exportingNotLoggedIn ? (
                      <><Spinner animation="border" size="sm" className="me-1" />Exporting...</>
                    ) : (
                      <><Download size={14} className="me-1" />Export</>
                    )}
                  </Button>
                </div>
              </div>

              {notLoggedInError && <Alert variant="danger">{notLoggedInError}</Alert>}

              {notLoggedInLoading ? (
                <div className="text-center my-4">
                  <Spinner animation="border" variant="danger" />
                </div>
              ) : notLoggedIn.length === 0 ? (
                <p className="text-muted text-center">All students have logged in.</p>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover table-bordered align-middle mb-3" style={{ fontSize: "0.875rem" }}>
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Contact</th>
                          <th>Degree</th>
                          <th>Year</th>
                          <th>Account Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notLoggedIn.map((student, idx) => (
                          <tr key={student.id}>
                            <td>{(notLoggedInPage - 1) * perPage + idx + 1}</td>
                            <td>{student.first_name} {student.last_name}</td>
                            <td>{student.email}</td>
                            <td>{student.contact_number}</td>
                            <td>{student.degree_title}</td>
                            <td>{student.studying_year}</td>
                            <td>{student.account_creation_date ? new Date(student.account_creation_date).toLocaleDateString("en-IN") : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {notLoggedInLastPage > 1 && (
                    <div className="d-flex justify-content-end mt-2">
                      <PaginationControls
                        currentPage={notLoggedInPage}
                        lastPage={notLoggedInLastPage}
                        onPageChange={(page) => fetchNotLoggedIn(page)}
                      />
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
      </div>
    </>
  );
};

export default StudentActivityDashboard;
